import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { PlatformService } from '@shared/services/platform-service.service';
import {
  ShippingValidationResult,
  ShippingValidationService,
} from '../../services/shipping-validation.service';

@Component({
  selector: 'app-shipping-warning',
  templateUrl: './shipping-warning.component.html',
  styleUrls: ['./shipping-warning.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
})
@UntilDestroy()
export class ShippingWarningComponent implements OnInit {
  articleId = input<number>();

  // Signals for reactive state management
  validationResult = signal<ShippingValidationResult | null>(null);
  isLoading = signal(false);
  isAvailableCountriesExpanded = signal(false);
  isUnavailableCountriesExpanded = signal(false);

  // Configuration for expandable lists
  readonly MAX_COUNTRIES_DISPLAY = 5;

  // Computed signals for derived state
  showWarning = computed(() => {
    const result = this.validationResult();
    if (!result) return false;

    // Show warning if:
    // 1. Article has a shipping rate but user has no shipping addresses
    // 2. Article has a shipping rate but shipping is not available to any user address
    // 3. Article has a shipping rate but shipping is only available to some user addresses
    return (
      result.hasShippingRate &&
      result.hasUserShippingAddresses &&
      (!result.isShippingAvailable || result.unavailableCountries.length > 0)
    );
  });

  warningType = computed((): 'danger' | 'warning' | 'info' => {
    const result = this.validationResult();
    if (!result) return 'info';

    if (!result.isShippingAvailable) {
      return 'danger'; // No shipping available at all
    } else if (result.unavailableCountries.length > 0) {
      return 'warning'; // Partial shipping availability
    }
    return 'info';
  });

  warningIcon = computed((): string => {
    const type = this.warningType();
    switch (type) {
      case 'danger':
        return 'fas fa-exclamation-triangle';
      case 'warning':
        return 'fas fa-exclamation-circle';
      default:
        return 'fas fa-info-circle';
    }
  });

  // Computed signals for expandable country lists
  availableCountriesDisplay = computed(() => {
    const result = this.validationResult();
    if (!result?.availableCountries) return [];

    const isExpanded = this.isAvailableCountriesExpanded();
    if (
      isExpanded ||
      result.availableCountries.length <= this.MAX_COUNTRIES_DISPLAY
    ) {
      return result.availableCountries;
    }

    return result.availableCountries.slice(0, this.MAX_COUNTRIES_DISPLAY);
  });

  showExpandAvailableCountries = computed(() => {
    const result = this.validationResult();
    return result?.availableCountries?.length > this.MAX_COUNTRIES_DISPLAY;
  });

  availableCountriesRemainingCount = computed(() => {
    const result = this.validationResult();
    if (!result?.availableCountries) return 0;
    return Math.max(
      0,
      result.availableCountries.length - this.MAX_COUNTRIES_DISPLAY
    );
  });

  // Computed signals for unavailable countries
  unavailableCountriesDisplay = computed(() => {
    const result = this.validationResult();
    if (!result?.unavailableCountries) return [];

    const isExpanded = this.isUnavailableCountriesExpanded();
    if (
      isExpanded ||
      result.unavailableCountries.length <= this.MAX_COUNTRIES_DISPLAY
    ) {
      return result.unavailableCountries;
    }

    return result.unavailableCountries.slice(0, this.MAX_COUNTRIES_DISPLAY);
  });

  showExpandUnavailableCountries = computed(() => {
    const result = this.validationResult();
    return result?.unavailableCountries?.length > this.MAX_COUNTRIES_DISPLAY;
  });

  unavailableCountriesRemainingCount = computed(() => {
    const result = this.validationResult();
    if (!result?.unavailableCountries) return 0;
    return Math.max(
      0,
      result.unavailableCountries.length - this.MAX_COUNTRIES_DISPLAY
    );
  });

  // Inject services
  private shippingValidationService = inject(ShippingValidationService);
  public authService = inject(AuthService);
  public languageService = inject(LanguageService);

  private readonly platformService = inject(PlatformService);

  constructor() {
    effect(() => {
      if (!this.articleId()) return;

      this.platformService.runOnBrowser(() => {
        this.authService
          .getUserLogged()
          .pipe(untilDestroyed(this))
          .subscribe((user) => {
            if (user && this.articleId()) {
              this.checkShippingAvailability();
            }
          });
      });
    });
  }

  ngOnInit(): void {
    // Initial check when component is initialized
    if (this.articleId()) {
      this.checkShippingAvailability();
    }
  }

  private checkShippingAvailability(): void {
    this.isLoading.set(true);
    this.shippingValidationService
      .validateShippingAvailability(this.articleId())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.validationResult.set(result);
          this.isLoading.set(false);
          // Reset expanded states when new data arrives
          this.isAvailableCountriesExpanded.set(false);
          this.isUnavailableCountriesExpanded.set(false);
        },
        error: (error) => {
          console.error('Error validating shipping:', error);
          this.isLoading.set(false);
        },
      });
  }

  getWarningType(): 'danger' | 'warning' | 'info' {
    return this.warningType();
  }

  getWarningIcon(): string {
    return this.warningIcon();
  }

  // Method to toggle available countries expansion
  toggleAvailableCountriesExpansion(): void {
    this.isAvailableCountriesExpanded.update((expanded) => !expanded);
  }

  // Method to toggle unavailable countries expansion
  toggleUnavailableCountriesExpansion(): void {
    this.isUnavailableCountriesExpanded.update((expanded) => !expanded);
  }
}
